import { z } from 'zod';
import { companyUidInputSchema, companyUidSchema } from '../../partner-cockpit/onboarding/validationStep.js';
import { validationDocumentTypeSchema } from './validationDocuments.js';
/**
 * What the craftsman is told about their own validation. Deliberately not the `TicketStatus` vocabulary: BackOps
 * statuses are an internal workflow, and translating at the boundary keeps the two free to diverge.
 */
export const partnerCockpitValidationStateSchema = z.enum([
    'open',
    'inValidation',
    'waitingOnInformation',
    'validated',
    'invalidPartner',
]);
export const PartnerCockpitValidationState = partnerCockpitValidationStateSchema.enum;
// While Partner Management is looking at the submission, or once it has ruled, the craftsman may not change it.
export const canEditPartnerCockpitValidation = (state) => state === PartnerCockpitValidationState.open || state === PartnerCockpitValidationState.waitingOnInformation;
export const canEditPendingCompanySettings = (state) => state === PartnerCockpitValidationState.open;
export const isValidationSelectionWithCraftsman = (ticketStatus) => ticketStatus === 'Open';
export const isPartnerCockpitValidated = (state) => state === undefined || state === PartnerCockpitValidationState.validated;
/**
 * Submitting hands the validation to Partner Management and locks it, so there has to be something in it to look at.
 * The rule matches the onboarding step's: a UID or at least one document, never both required.
 */
export const canSubmitPartnerCockpitValidation = ({ state, hasUid, hasDocuments, }) => canEditPartnerCockpitValidation(state) && (hasUid || hasDocuments);
// A validated craftsman's identity documents are purged, so the section has nothing left to show.
export const showsIdentityDocuments = (state) => state !== PartnerCockpitValidationState.validated && state !== PartnerCockpitValidationState.invalidPartner;
// No url: the objects sit in a private bucket and the cockpit deliberately offers no way to read one back.
export const partnerCockpitValidationDocumentSchema = z.object({
    uuid: z.uuid(),
    type: validationDocumentTypeSchema,
    fileName: z.string(),
    mimeType: z.string(),
    size: z.number().nullable(),
    uploadedAt: z.iso.datetime(),
});
export const partnerCockpitValidationSchema = z.object({
    // Identifies this specific validation ticket, as opposed to `state` which is just the current status —
    // a partner can go through several validation tickets over time (e.g. after cancelling and resubmitting),
    // and each one should get its own banner-dismissal history rather than sharing one keyed only by state.
    ticketUuid: z.uuid(),
    state: partnerCockpitValidationStateSchema,
    uid: companyUidSchema.nullable(),
    documents: z.array(partnerCockpitValidationDocumentSchema),
});
/**
 * Null until Partner Management opens a validation for the partner, which happens outside the cockpit. Wrapped in an
 * object so the absent case is a normal body rather than a bare `null`.
 */
export const partnerCockpitValidationResponseSchema = z.object({
    validation: partnerCockpitValidationSchema.nullable(),
});
export const savePartnerCockpitValidationSchema = z.object({
    // The digits as typed, `CHE-` is prefilled by the UI as in the cockpit's onboarding step.
    uid: companyUidInputSchema.nullish(),
});
